import { isBoolean, isEnumOfType, isObjectLike, isOptional, isOptionalArrayOf, isString, TypeGuard } from '../lib/typeInference.js';

export enum NotificationType {
    Alert = 'alert',
    OrderFailed = 'order_failed',
    OrderCompleted = 'order_completed',
    Topup = 'topup', // topups have been dropped
    Credit = 'credit',
    Survey = 'survey',
    Referral = 'referral'
}

export enum NotificationMessageStatus {
    Draft = 'DRAFT', // Saved, but not sent
    Pending = 'PENDING',
    Done = 'DONE',
    Error = 'ERROR'
}

export interface INotificationMessageDeeplinkBase {
    moduleId: string;
    moduleItemId: string;
}

export interface INotificationMessageDeeplinkVoucher extends INotificationMessageDeeplinkBase {
    voucher?: string;
}

export type INotificationMessageDeeplink = INotificationMessageDeeplinkBase | INotificationMessageDeeplinkVoucher;

function isNotificationMessageDeeplink(obj: any): obj is INotificationMessageDeeplink {
    return isNotificationMessageDeeplinkVoucher(obj) || isNotificationMessageDeeplinkBase(obj);
}

function isNotificationMessageDeeplinkBase(obj: any): obj is INotificationMessageDeeplinkBase {
    return obj && isString(obj.moduleId) && isString(obj.moduleItemId);
}

function isNotificationMessageDeeplinkVoucher(obj: any): obj is INotificationMessageDeeplinkVoucher {
    return isOptional(isString, obj.voucher) && isNotificationMessageDeeplinkBase(obj);
}

export type INotificationMessageSource =
    | {
          source: 'USER_METRIC';
          definitionId: string;
      }
    | {
          source: 'SCHEDULED_TASK';
          scheduledTaskId: string;
      }
    | {
          source: 'RULE';
          ruleId: string;
      }
    | {
          source: 'API';
      };

export function isNotificationMessageSource(obj: unknown): obj is INotificationMessageSource {
    return (
        isObjectLike(obj) &&
        isString(obj.source) &&
        ((obj.source === 'USER_METRIC' && isString(obj.definitionId)) ||
            (obj.source === 'SCHEDULED_TASK' && isString(obj.scheduledTaskId)) ||
            (obj.source === 'RULE' && isString(obj.ruleId)) ||
            obj.source === 'API')
    );
}

export interface ICreateNotification<IDTYPE> {
    title?: string;
    message: string;
    imageUrl?: string;
    deepLink?: INotificationMessageDeeplink;
    buttonText?: string;
    type: NotificationType;
    value: string; // TODO - what are the values?  These need encoding as pepper-value
    isProcessingMessage: boolean; // if false, then won't be sent to users who have set marketing Opt-out
    userIds?: IDTYPE[];
    segmentIds?: IDTYPE[];
    locationIds?: IDTYPE[];
    global?: boolean;
    source: INotificationMessageSource;
    status?: NotificationMessageStatus.Draft | NotificationMessageStatus.Pending;
    tag?: string;
}

export function isCreateNotificationRequest<IDTYPE>(obj: unknown, isIDTYPE: TypeGuard<IDTYPE>): obj is Omit<ICreateNotification<IDTYPE>, 'source'> {
    return (
        isObjectLike(obj) &&
        isOptional(isString, obj.title) &&
        isString(obj.message) &&
        isOptional(isString, obj.imageUrl) &&
        isOptional(isNotificationMessageDeeplink, obj.deepLink) &&
        isOptional(isString, obj.buttonText) &&
        isEnumOfType(NotificationType, obj.type) &&
        isString(obj.value) &&
        isBoolean(obj.isProcessingMessage) &&
        isOptionalArrayOf(isIDTYPE, obj.userIds) &&
        isOptionalArrayOf(isIDTYPE, obj.segmentIds) &&
        isOptionalArrayOf(isIDTYPE, obj.locationIds) &&
        isOptional(isBoolean, obj.global) &&
        (!obj.status || obj.status === NotificationMessageStatus.Draft || obj.status === NotificationMessageStatus.Pending) &&
        isOptional(isString, obj.tag)
    );
}

export function isCreateNotification<IDTYPE>(obj: unknown, isIDTYPE: TypeGuard<IDTYPE>): obj is ICreateNotification<IDTYPE> {
    return isCreateNotificationRequest(obj, isIDTYPE) && isNotificationMessageSource((obj as any).source);
}

export type ICreateNotificationFromScheduledTask<IDTYPE> = Omit<ICreateNotification<IDTYPE>, 'source'>;

export function isCreateNotificationFromScheduledTask<IDTYPE>(obj: unknown, isIDTYPE: TypeGuard<IDTYPE>): obj is ICreateNotificationFromScheduledTask<IDTYPE> {
    return isCreateNotificationRequest(obj, isIDTYPE);
}

export interface INotificationMessageResult {
    status: NotificationMessageStatus;
    sent: number;
    failures: number;
    skipped: number;
    errorMessage?: string;
}

export interface INotificationMessage<IDTYPE> extends INotificationMessageResult, Omit<ICreateNotification<IDTYPE>, 'status'> {
    _id: IDTYPE;
    tenantId: IDTYPE;
    createdAt: Date;
    updatedAt: Date;
    completedAt?: Date;
}

export enum NotificationReceiptStatus {
    SENT = 'sent',
    ERROR = 'error',
    SKIPPED = 'skipped'
}

export interface INotificationReceipt<IDTYPE> {
    _id: IDTYPE;
    tenantId: IDTYPE;
    notificationMessageId: IDTYPE;
    userId: IDTYPE;
    userFullName?: string;
    sentAt: Date;
    method?: string; // APNS / GCM / FCM
    status: NotificationReceiptStatus;
    pushMessageId?: string;
    skipMessage?: string;
    errorMessage?: string;
    createdAt: Date;
}

export type INotificationReceiptCreate<IDTYPE> = Omit<INotificationReceipt<IDTYPE>, '_id'>;
